import TomSelect from 'tom-select/base';
import clearButton from 'tom-select/plugins/clear_button/plugin.js';
import removeButton from 'tom-select/plugins/remove_button/plugin.js';

TomSelect.define('clear_button', clearButton);
TomSelect.define('remove_button', removeButton);

function create(element, options = {}) {
    if (element.tomselect) return element.tomselect;
    if (!element.labels.length && !element.hasAttribute('aria-label') && options.placeholder) {
        element.setAttribute('aria-label', options.placeholder);
    }
    return new TomSelect(element, {
        create: false,
        clearAfterSelect: true,
        closeAfterSelect: true,
        maxOptions: null,
        copyClassesToDropdown: false,
        // Keep the server's song ordering when no search term is entered.
        sortField: [{ field: '$order' }, { field: '$score' }],
        plugins: element.multiple ? {
            remove_button: {
                html: () => {
                    const button = document.createElement('button');
                    button.type = 'button';
                    button.className = 'remove';
                    button.setAttribute('aria-label', 'Remove item');
                    button.textContent = '×';
                    return button;
                },
            },
        } : {},
        ...options,
    });
}

function enhance(selector, options = {}, root = document) {
    return selectElements(selector, root).map(element => create(element, options));
}

function selectElements(selector, root) {
    // Tom Select copies source classes to its wrapper. Only initialize selects.
    return Array.from(root.querySelectorAll('select')).filter(element => element.matches(selector));
}

function remote(selector, { url, placeholder }, root = document) {
    return selectElements(selector, root).map(element => {
        if (element.tomselect) return element.tomselect;
        let controller;
        let destroyed = false;
        let failed = false;
        return create(element, {
            placeholder,
            plugins: {
                clear_button: {
                    html: () => '<button type="button" class="clear-button" aria-label="Clear selection">×</button>',
                },
            },
            loadThrottle: 180,
            refreshThrottle: 0,
            shouldLoad: query => query.length >= 1,
            // The endpoint searches names and IDs; don't filter its results again.
            searchField: [],
            hideSelected: true,
            onType() {
                failed = false;
                controller?.abort();
                this.clearOptions();
                this.refreshOptions(false);
            },
            async load(query, callback) {
                if (destroyed) return;
                if (query !== this.control_input.value) return callback();
                controller?.abort();
                const request = new AbortController();
                controller = request;
                const endpoint = new URL(url, window.location.href);
                endpoint.searchParams.set('q', query);
                try {
                    const response = await fetch(endpoint, {
                        signal: request.signal,
                        credentials: 'same-origin',
                        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    });
                    if (!response.ok) throw new Error('Search request failed');
                    const items = await response.json();
                    if (!Array.isArray(items)) throw new Error('Invalid search response');
                    if (destroyed) return;
                    if (request.signal.aborted || query !== this.control_input.value) return callback();
                    callback(items.map(item => ({ value: String(item.id), text: item.label })));
                } catch {
                    if (destroyed) return;
                    failed = !request.signal.aborted;
                    // A failed/aborted request must not cache an empty result.
                    delete this.loadedSearches[query];
                    callback();
                }
            },
            render: {
                no_results: () => {
                    const message = document.createElement('div');
                    message.className = 'no-results';
                    message.textContent = failed ? 'Unable to load results. Try again.' : 'No results found';
                    return message;
                },
            },
            onDestroy() {
                destroyed = true;
                controller?.abort();
            },
        });
    });
}

window.SelectInput = { enhance, remote };
